<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const showSuspendPopup = ref(false);
const showUnsuspendPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const suspendConfirmText = computed(
  () => `${t('AGENT_MGMT.SUSPEND.CONFIRM.YES')} ${currentAgent.value.name}`
);
const suspendRejectText = computed(() => {
  return `${t('AGENT_MGMT.SUSPEND.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const suspendMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const unsuspendConfirmText = computed(
  () => `${t('AGENT_MGMT.UNSUSPEND.CONFIRM.YES')} ${currentAgent.value.name}`
);
const unsuspendRejectText = computed(() => {
  return `${t('AGENT_MGMT.UNSUSPEND.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const unsuspendMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const lastActiveAt = agent => {
  if (agent.active_at) {
    return new Date(agent.active_at).toLocaleString();
  }
  return '';
};

const suspendedAt = agent => {
  if (agent.suspended_at) {
    return new Date(agent.suspended_at).toLocaleString();
  }
  return '';
};

const labelLastActive = 'Ultima ação em';
const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showSuspendAction = agent => {
  return (
    currentUserId.value !== agent.id && agent.confirmed && !agent.suspended_at
  );
};

const showUnsuspendAction = agent => {
  return (
    currentUserId.value !== agent.id && agent.confirmed && agent.suspended_at
  );
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};

const openSuspendPopup = agent => {
  showSuspendPopup.value = true;
  currentAgent.value = agent;
};

const closeSuspendPopup = () => {
  showSuspendPopup.value = false;
};

const openUnsuspendPopup = agent => {
  showUnsuspendPopup.value = true;
  currentAgent.value = agent;
};

const closeUnsuspendPopup = () => {
  showUnsuspendPopup.value = false;
};

const suspendAgent = async id => {
  try {
    await store.dispatch('agents/suspend', id);
    showAlertMessage(t('AGENT_MGMT.SUSPEND.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.SUSPEND.API.ERROR_MESSAGE'));
  }
};

const unsuspendAgent = async id => {
  try {
    await store.dispatch('agents/unsuspend', id);
    showAlertMessage(t('AGENT_MGMT.UNSUSPEND.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.UNSUSPEND.API.ERROR_MESSAGE'));
  }
};

const confirmSuspension = () => {
  loading.value[currentAgent.value.id] = true;
  closeSuspendPopup();
  suspendAgent(currentAgent.value.id);
};

const confirmUnsuspension = () => {
  loading.value[currentAgent.value.id] = true;
  closeUnsuspendPopup();
  unsuspendAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        feature-name="agents"
      >
        <template #actions>
          <woot-button
            class="rounded-md button nice"
            icon="add-circle"
            @click="openAddPopup"
          >
            {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
          </woot-button>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="divide-y divide-slate-75 dark:divide-slate-700">
        <tbody
          class="divide-y divide-slate-50 dark:divide-slate-800 text-slate-700 dark:text-slate-300"
        >
          <tr v-for="(agent, index) in agentList" :key="agent.email">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row items-center gap-4">
                <Thumbnail
                  :src="agent.thumbnail"
                  :username="agent.name"
                  size="40px"
                  :status="agent.availability_status"
                />
                <div>
                  <span class="block font-medium capitalize">
                    {{ agent.name }}
                  </span>
                  <span>{{ agent.email }}</span>
                </div>
              </div>
            </td>

            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row items-center gap-4">
                <span>{{ labelLastActive }} {{ lastActiveAt(agent) }}</span>
              </div>
            </td>

            <td class="relative py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="block font-medium w-fit"
                :class="{
                  'hover:text-gray-900 group cursor-pointer':
                    agent.custom_role_id,
                }"
              >
                {{ getAgentRoleName(agent) }}

                <div
                  class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-slate-50 shadow-lg top-14 md:top-12 dark:bg-slate-800 dark:border-slate-700"
                  :class="{ 'group-hover:block': agent.custom_role_id }"
                >
                  <div class="flex flex-col gap-1 p-4">
                    <span class="font-semibold">
                      {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                    </span>
                    <ul class="pl-4 mb-0 list-disc">
                      <li
                        v-for="permission in getAgentRolePermissions(agent)"
                        :key="permission"
                        class="font-normal"
                      >
                        {{
                          $t(
                            `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                          )
                        }}
                      </li>
                    </ul>
                  </div>
                </div>
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span
                v-if="agent.suspended_at"
                v-tooltip.top="'Inativado em: ' + suspendedAt(agent)"
                class="text-red-600 font-medium cursor-help"
              >
                {{ $t('AGENT_MGMT.LIST.SUSPENDED') }}
              </span>
              <span v-else-if="agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
              </span>
              <span v-else>
                {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
              </span>
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <woot-button
                  v-if="showEditAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  icon="edit"
                  class-names="grey-btn"
                  @click="openEditPopup(agent)"
                />
                <woot-button
                  v-if="showSuspendAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.SUSPEND.BUTTON_TEXT')"
                  variant="smooth"
                  color-scheme="warning"
                  size="tiny"
                  icon="account-circle-off"
                  class-names="grey-btn"
                  :is-loading="loading[agent.id]"
                  @click="openSuspendPopup(agent)"
                />
                <woot-button
                  v-if="showUnsuspendAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.UNSUSPEND.BUTTON_TEXT')"
                  variant="smooth"
                  color-scheme="success"
                  size="tiny"
                  icon="account-circle"
                  class-names="grey-btn"
                  :is-loading="loading[agent.id]"
                  @click="openUnsuspendPopup(agent)"
                />
                <woot-button
                  v-if="showDeleteAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                  variant="smooth"
                  color-scheme="alert"
                  size="tiny"
                  icon="dismiss-circle"
                  class-names="grey-btn"
                  :is-loading="loading[agent.id]"
                  @click="openDeletePopup(agent, index)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-delete-modal
      v-model:show="showSuspendPopup"
      :on-close="closeSuspendPopup"
      :on-confirm="confirmSuspension"
      :title="$t('AGENT_MGMT.SUSPEND.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.SUSPEND.CONFIRM.MESSAGE')"
      :message-value="suspendMessage"
      :confirm-text="suspendConfirmText"
      :reject-text="suspendRejectText"
    />

    <woot-delete-modal
      v-model:show="showUnsuspendPopup"
      :on-close="closeUnsuspendPopup"
      :on-confirm="confirmUnsuspension"
      :title="$t('AGENT_MGMT.UNSUSPEND.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.UNSUSPEND.CONFIRM.MESSAGE')"
      :message-value="unsuspendMessage"
      :confirm-text="unsuspendConfirmText"
      :reject-text="unsuspendRejectText"
    />
  </SettingsLayout>
</template>
